"use client";

import React, { useState, useEffect, useRef, MouseEvent } from "react";
import { apiFetch } from "@/utils/api";
import {
  ClockIcon,
  CopyIcon,
  ExternalLinkIcon,
  StarIcon,
  UsersIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@/utils/icons";
import Image from "next/image";
import { getSafeImageUrl } from "@/utils/images";
import {  usePathname } from "next/navigation";

interface Offer {
  _id?: string;
  store: string;
  storeIcon?: string;
  title: string;
  slug: string;
  description: string;
  discount: string;
  discountType: string;
  discountTypeFlat: string;
  discountTypeUnit: string;
  oldValue: string;
  oldValueUnit: string;
  category: string;
  couponCode: string;
  validTill: string;
  usedBy: number;
  rating: number;
  color: string;
  badge: string;
  bgGradient: string;
  isActive: boolean;
  allowRedirectUrl: boolean;
  redirectUrl: string;
}

function isValidOffer(obj: unknown): obj is Offer {
  if (!obj || typeof obj !== "object") return false;
  const o = obj as Record<string, unknown>;
  return (
    typeof o.title === "string" &&
    typeof o.couponCode === "string"
    // && typeof o.validTill === "string"
  );
}

interface PopularOffersSectionProps {
  slug?: string;
}

const PopularOffersSection: React.FC<PopularOffersSectionProps> = ({
  slug,
}) => {
  // const router = useRouter();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [imgErrorMap, setImgErrorMap] = useState<{ [key: string]: boolean }>(
    {}
  );

  const mobileStoresRef = useRef<HTMLDivElement>(null);
  const location = usePathname()
  const [isInStore, setIsInStore] = useState(false);

  useEffect(() => {
    if (location && location.includes('/store/')) {
      setIsInStore(true);
    } else {
      setIsInStore(false);
    }
  }, [location]);



  const scrollMobileOffers = (direction: 'left' | 'right') => {
    if (mobileStoresRef.current) {
      // Adjusted scroll amount for horizontal scroll on mobile/tablet
      const isDesktop = window.innerWidth >= 768;
      // Scroll by roughly 2 cards + gap on desktop/tablet, or 2 cards + gap on mobile
      const scrollAmount = isDesktop ? (20 * 16) + 24 : (10 * 16) * 2 + 16;
      const currentScroll = mobileStoresRef.current.scrollLeft;

      if (direction === 'left') {
        mobileStoresRef.current.scroll({
          left: currentScroll - scrollAmount,
          behavior: 'smooth'
        });
      } else {
        mobileStoresRef.current.scroll({
          left: currentScroll + scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };


  useEffect(() => {
    let mounted = true;
    setLoading(true);
    let endpoint = "";
    //NEXT_PUBLIC_APP_TITLE=OfferCounty
    if (process.env.NEXT_PUBLIC_APP_TITLE === "OfferCounty") {
      endpoint = slug ? `/offers/store/${slug}` : "/offers?minRating=4.5&discountType=code&usedBy=30&isFeatured=true";
    }else{
      endpoint = slug ? `/offers/store/${slug}` : "/offers?minRating=4.5&usedBy=30&isFeatured=true";
    }
    apiFetch<unknown>(endpoint)
      .then((res) => {
        let arr: unknown = [];
        if (Array.isArray(res)) {
          arr = res;
        } else if (res && typeof res === "object") {
          const obj = res as Record<string, unknown>;
          if (
            obj.data &&
            typeof obj.data === "object" &&
            Array.isArray((obj.data as Record<string, unknown>).offers)
          ) {
            arr = (obj.data as Record<string, unknown>).offers;
          } else if (Array.isArray(obj.offers)) {
            arr = obj.offers;
          } else if (Array.isArray(obj.data)) {
            arr = obj.data;
          }
        }
        // Only show offers with usedBy > 30 or rating >= 4.5
        let validOffers = (Array.isArray(arr) ? arr : [])
          .filter(
            (o) =>
              isValidOffer(o) &&
              (
                (typeof o.usedBy === "number" && o.usedBy > 30) ||
                (o.discountType === "code") &&
                (typeof o.rating === "number" && o.rating >= 4.5)
              )
          );

        // Fallback to all code-type offers if the filtered list is empty
        validOffers =
          validOffers.length > 0 ? validOffers : (Array.isArray(arr) ? arr : []).filter(
            (o) =>
              isValidOffer(o) &&
              (o.discountType === "code"));

        if (mounted) {
          setOffers(validOffers);
        }
      })
      .catch((err: unknown) => {
        const message =
          err && typeof err === "object" && "message" in err
            ? (err as { message?: string }).message
            : undefined;
        if (mounted) {
          setError(message || "Failed to load offers");
          setOffers([]);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  const handleCopy = async (code: string) => {
    let success = false;
    // Try Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(code);
        success = true;
      } catch { }
    }
    // Fallback for non-secure or unsupported browsers
    if (!success) {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
        document.execCommand("copy"); // Ensure copy happens
        success = true;
      } catch { }
      document.body.removeChild(textarea);
    }
    if (success) {
      setCopied(code);
      setTimeout(() => setCopied(null), 1200);
    }
  };


 const handleCouponActionClick = (offer: Offer) => {
    if (offer.couponCode) {
      handleCopy(offer.couponCode);
    }
    const internalOfferUrl = `/offers/${offer.slug}`;
    const couponLoaderUrl = `/coupon-loader?slug=${offer.slug}`;

    window.open(internalOfferUrl, "_blank", "noopener,noreferrer");
    window.location.href = couponLoaderUrl;
};

const handleGeneralActionClick = (offer: Offer) => {
    if (offer.couponCode) {
      handleCopy(offer.couponCode);
    }
    const internalOfferUrl = `/offers/${offer.slug}`;
    const couponLoaderUrl = `/coupon-loader?slug=${offer.slug}`;

    window.open(couponLoaderUrl, "_blank", "noopener,noreferrer");

    window.location.href = internalOfferUrl;
};
  
  // *** UNIFIED DEAL CLICK HANDLER ***
  const handleDealClick = (offer: Offer, e?: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    // Prevent action if a child button was clicked (since the whole card is clickable)
    const targetIsInteractive = e?.target instanceof HTMLElement && (e.target.tagName === 'BUTTON' || e.target.closest('button'));
    if (e && targetIsInteractive) {
      return;
    }
    
    const isCouponCode = offer.discountType.toLowerCase() === 'code' && offer.couponCode;

    if (isCouponCode) {
      handleCouponActionClick(offer);
    } else {
      handleGeneralActionClick(offer);
    }
  };


  const RenderDiscountPercentage = (offer: Offer) => {
    if (!offer.oldValue || !offer.discount) return null;

    const oldVal = Number(offer.oldValue);
    const discountVal = Number(offer.discount);

    if (isNaN(oldVal) || isNaN(discountVal)) return null;

    if (offer.discountTypeUnit === "percentage" && offer.oldValueUnit === "price") {
      const discountAmount = (oldVal * discountVal) / 100;
      const newVal = oldVal - discountAmount;
      if (newVal <= 0)
        return <p className="font-bold text-red-600">100% off</p>;

      const percentage = Math.round(((oldVal - newVal) / oldVal) * 100);

      return (
        <div className="w-full">
          <p className="gap-1 flex items-baseline text-xs ">
            <span className="text-base font-semibold text-primary-700">
              ₹{newVal.toFixed(2)}
            </span>
            <span className="line-through text-gray-500">
              ₹{oldVal.toFixed(2)}
            </span>
            <span className="font-medium text-primary-600">
              {percentage}% off
            </span>{" "}
          </p>
        </div>
      );
    }

    if (offer.discountTypeUnit === "price" && offer.oldValueUnit === "price") {
      const newVal = oldVal - discountVal;
      if (newVal <= 0)
        return <p className="font-bold text-red-600">100% off</p>;

      const percentage = Math.round(((oldVal - newVal) / oldVal) * 100);

      return (
        <div className="">
          <p className="gap-1 flex items-baseline text-xs ">
            <span className="text-base font-semibold text-primary-700">
              ₹{newVal.toFixed(2)}
            </span>
            <span className="line-through text-gray-500">
              ₹{oldVal.toFixed(2)}
            </span>
            <span className="font-medium text-primary-600">
              {percentage}% off
            </span>{" "}
          </p>
        </div>
      );
    }

    if (offer.discountTypeUnit === "percentage" && offer.oldValueUnit === "percentage") {
      const change = Math.abs(oldVal - discountVal);
      const direction = oldVal > discountVal ? "↓" : "↑";
      const percentageChange = ((change / oldVal) * 100).toFixed(1);

      return (
        <p className="gap-1 text-sm font-medium text-primary-600">
          <span className="line-through text-gray-500">
            {oldVal}%
          </span>{" "}
          →
          <span className=" text-base font-medium text-primary-600">
            {discountVal}%{" "}
          </span>
          <span className="ml-1 text-xs">
            {direction} {percentageChange}%
          </span>
        </p>
      );
    }

    if (offer.discountTypeUnit === "price" && offer.oldValueUnit === "percentage") {
      // const newVal = oldVal - discountVal;
      return (
        <p className="text-sm font-medium text-primary-600">
          <span className="line-through">
            {oldVal}%
          </span>{" "} → ₹{discountVal}{" "}
          <span className="text-gray-500 text-xs ml-1">(Fixed drop)</span>
        </p>
      );
    }

    return null;
  };

  if (slug && offers.length < 1) return null;
  if (slug && isInStore && offers.length < 4) return null;

  return (
    <div className="">
      <div className="mb-4 lg:mb-6 flex items-center justify-between">
        <div className="inline-block">
          <h2 className="relative text-2xl text-gray-700">Popular Offers</h2>
          <hr className="h-1 border-0 bg-primary-500 mt-2 w-[50%] rounded-lg" />
        </div>
        {/* Scroll Buttons for Mobile/Tablet ONLY (Hidden on large desktop) */}
        <div className="flex gap-2">
          <button
            onClick={() => scrollMobileOffers('left')}
            className="p-1 bg-gray-100 rounded-full shadow-md text-gray-600 hover:bg-gray-200 transition-colors"
            aria-label="Scroll Offers Left"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => scrollMobileOffers('right')}
            className="p-1 bg-gray-100 rounded-full shadow-md text-gray-600 hover:bg-gray-200 transition-colors"
            aria-label="Scroll Offers Right"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div
        className="relative flex-1 overflow-x-auto scrollbar-hide scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
        ref={mobileStoresRef}
      >
        <div
          // Desktop uses horizontal scrolling flex row
          className="grid grid-flow-col-dense auto-cols-[10rem] grid-rows-2 lg:gap-4 gap-2 pb-2 min-h-[20rem]
                       md:flex md:flex-row md:flex-nowrap md:gap-6 md:w-max md:h-auto 
                       md:grid-rows-none md:grid-flow-row md:auto-cols-auto px-2"
        >
          {loading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                // Mobile sizing (Default) | Desktop sizing (md+)
                className="animate-pulse bg-gray-200 rounded-xl w-full min-w-[10rem] h-[12.5rem] flex-shrink-0 
                           md:w-80 md:h-72 md:min-w-[20rem]"
                aria-busy="true"
              />
            ))}
          {!loading && error && (
            <div className="text-center text-red-500 py-8 min-w-[10rem] md:min-w-[18rem] md:w-full">
              <div className="text-4xl mb-2">❌</div>
              <div className="font-semibold">{error}</div>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded font-semibold hover:bg-primary-700 transition"
              >
                Retry
              </button>
            </div>
          )}
          {!loading && !error && offers.length === 0 && (
            <div className="text-center text-gray-500 py-8 min-w-[10rem] md:min-w-[18rem] md:w-full">
              <div className="text-4xl mb-2">🎁</div>
              <div className="font-semibold">No offers found.</div>
            </div>
          )}
          {!loading &&
            !error &&
            offers.map((offer, i) => (
              <div key={offer._id || i}>
                {/* DESKTOP UI 
                */}
                <div
                  // Connects to unified handler
                  onClick={(e) => handleDealClick(offer, e)}
                  role="button"
                  tabIndex={0}
                  className={`hidden md:flex md:flex-col relative bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-2xl overflow-hidden flex-shrink-0 
                              w-80 h-72 min-w-[20rem] group 
                              cursor-pointer transform transition-all duration-300 hover:translate-y-1 hover:border-primary-400`}
                  aria-label={offer.title}
                >
                  {offer.badge && (
                    <div
                      className="absolute top-0 right-0 z-10 px-3 py-1 text-xs font-bold text-white bg-gradient-to-r from-primary-500 to-cyan-400  rounded-bl-xl shadow-lg"
                    >
                      {offer.badge}
                    </div>
                  )}

                  {/* 1. Main Content Area (Upper 70% of Card) */}
                  <div className="flex-1 p-4 flex flex-col justify-start">

                    {/* Store Icon & Store Name (Top) */}
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-1 bg-white rounded-md border border-gray-100 shadow-sm">
                        <Image
                          src={
                            imgErrorMap[offer._id || offer.store]
                              ? "/assets/OfferCodeFlight.jpg"
                              : getSafeImageUrl(offer.storeIcon)
                          }
                          alt={offer.store}
                          width={80}
                          height={80}
                          className="w-20 h-16 object-contain rounded-sm"
                          onError={() =>
                            setImgErrorMap((map) => ({
                              ...map,
                              [offer._id || offer.store]: true,
                            }))
                          }
                        />
                      </div>
                      <span className="font-semibold text-gray-500 capitalize line-clamp-1 text-sm">
                        {offer.store.replaceAll("-", " ")}
                      </span>
                    </div>

                    {/* Title - The main headline */}
                    <h3 className="text-lg font-bold text-gray-800 line-clamp-2 mb-1">
                      {offer.title}
                    </h3>

                    {/* Description/Offer Details */}
                    <p className="text-sm text-gray-600 line-clamp-2 flex-1">
                      {offer.description}
                    </p>
                  </div>

                  {/* 2. Action Footer (Bottom 30% of Card - Dedicated Action Area) */}
                  <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-shrink-0">

                    {/* Usage & Rating Metrics */}
                    <div className="flex flex-col text-xs text-gray-500 min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center">
                          <StarIcon className="w-3 h-3 text-yellow-500 mr-0.5" />
                          {offer.rating ? offer.rating.toFixed(1) : 'N/A'}
                        </span>
                        <span className="flex items-center">
                          <UsersIcon className="w-3 h-3 text-primary-500 mr-0.5" />
                          {offer.usedBy}+ used
                        </span>
                      </div>
                      {/* Optional: Add Expiry or Coupon Code text here if needed */}
                      {offer.validTill && (
                        <span className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                          <ClockIcon className="w-3 h-3" />
                          Valid Till: {new Date(offer.validTill).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    {/* Action Button - DESKTOP  */}
                    <button
                        className={`text-sm font-bold text-white py-2 px-4 rounded-lg shadow-md bg-primary-600 hover:bg-primary-700 transition-all duration-300 flex items-center justify-center flex-shrink-0 
                                  `}
                        onClick={(e) => {
                            e.stopPropagation(); 
                            if (offer.couponCode) {
                                handleCouponActionClick(offer); 
                            } else {
                                handleGeneralActionClick(offer); 
                            }
                        }}
                    >
                        {/*  display "GET OFFER" or "GET DEAL" */}
                        {offer.couponCode ? 'GET CODE' : 'GET DEAL'} 
                        {offer.couponCode && copied === offer.couponCode && (
                            <CopyIcon className="w-4 h-4 ml-1" />
                        )}
                    </button>
                  </div>
                </div>


                {/* MOBILE UI 
                */}
                < div
                  // Connects to unified handler (Mobile/smaller than 768px)
                  onClick={(e) => {
                    if (window.innerWidth < 768) {
                      handleDealClick(offer, e);
                    }
                  }}
                  role={window.innerWidth < 768 ? "button" : undefined}
                  tabIndex={window.innerWidth < 768 ? 0 : undefined}
                  className={`md:hidden relative bg-white border border-gray-200 rounded-xl shadow-lg flex flex-col justify-between p-3 transition-all duration-300 group hover:shadow-2xl hover:border-primary-400 focus-within:shadow-2xl focus-within:border-primary-500 animate-fadein
                    w-[9.3rem] h-[9.5rem] flex-shrink-0
                    ${window.innerWidth < 768 ? 'cursor-pointer hover:scale-105' : 'hover:scale-95'}`}

                  aria-label={offer.title}
                >
                  {offer.badge && (
                    <div
                      className="absolute -top-3 -left-3 z-10 px-3 py-1 text-xs font-bold text-white bg-gradient-to-br from-cyan-400 to-primary-500 shadow-lg rounded-lg transform border-2 border-primary-200 hidden md:block"
                      style={{
                        boxShadow: "0 4px 16px rgba(99,102,241,0.15)",
                        borderRadius:
                          "1rem 1rem 1rem 0.5rem/1.5rem 0.5rem 1.5rem 0.5rem",
                      }}
                    >
                      {offer.badge}
                    </div>
                  )}

                  <div className="flex flex-col gap-1 md:gap-2 flex-1">

                    <div className="flex items-start gap-2 mb-1 justify-between ">
                      <div className="flex flex-col items-start gap-1 min-w-0">

                        {/* Store Name */}
                        <span className="font-bold text-xs md:text-sm py-3 text-gray-700 line-clamp-1 capitalize">
                          {offer.store.replaceAll("-", " ")}
                        </span>
                      </div>
                      {/* Store Icon with Next.js Image */}
                      <Image
                        src={
                          imgErrorMap[offer._id || offer.store]
                            ? "/assets/OfferCodeFlight.jpg"
                            : getSafeImageUrl(offer.storeIcon)
                        }
                        alt={offer.store}
                        width={32}
                        height={32}
                        className="w-8 h-8 md:w-14 md:h-14 object-contain rounded-md border border-gray-200 bg-white ml-2"
                        onError={() =>
                          setImgErrorMap((map) => ({
                            ...map,
                            [offer._id || offer.store]: true,
                          }))
                        }
                      />
                    </div>
                    {/* Title */}
                    <p className="text-primary-700 text-sm md:text-base md:mb-2 line-clamp-2 min-h-[1.5rem] md:line-clamp-1">
                      {offer.title}
                    </p>
                    <p className=" text-gray-600 text-xs sm:text-[10px] mb-2 line-clamp-2 md:text-xs md:mb-2 md:line-clamp-2">
                      {offer.description}
                    </p>

                       {offer.discountType.toLowerCase() === "general" && (
                    <div className="hidden md:flex w-full flex-col items-center mb-4">
                      {offer.discount && offer.oldValue && (
                        <div className="hover:transition-all hover:duration-300 hover:ease-in-out hover:scale-[1.01] flex w-full">
                          {RenderDiscountPercentage(offer)}
                        </div>
                      )}
                      {offer.discount ?
                      <div className="capitalize w-full text-sm bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg p-3">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Offer</p>
                          <p
                            className={`text-primary-700 font-bold text-xs transition-all duration-300 }`}
                          >
                            Get {offer.discountTypeFlat} {offer.discount}{" "}
                            {offer.discountTypeUnit === "percentage" ? "%" : "₹"} Off
                          </p>
                        </div>
                      </div>
                      :
                      <div className="capitalize w-full text-sm bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg p-3">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Offer</p>
                          <p
                            className={`text-primary-700 font-bold text-xs transition-all duration-300 }`}
                          >
                            OFFER ACTIVATED
                          </p>
                        </div>
                      </div>
                        }
                    </div>
                  )}

                  {offer.couponCode &&
                    offer.discountType.toLowerCase() === "code" && (
                      <div className="hidden md:flex bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg p-3 mb-2 items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">
                            Coupon Code
                          </p>
                          <p
                            className={`font-bold text-gray-900 transition-all duration-300 select-none ${
                              copied === offer.couponCode
                                ? "blur-none"
                                : "blur bg-gradient-to-r from-gray-300 to-gray-100 "
                            }`}
                            aria-label={
                              copied === offer.couponCode
                                ? offer.couponCode
                                : "Hidden coupon code"
                            }
                          >
                            {offer.couponCode}
                          </p>
                        </div>
                        {/* Button still calls handleCouponActionClick, but UI has no "GET CODE" text */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); 
                            handleCouponActionClick(offer); 
                          }}
                          className="flex items-center space-x-1 bg-white border border-gray-300 px-3 py-1 rounded-md hover:bg-gray-50 transition-colors duration-200 text-xs font-medium"
                        >
                          <CopyIcon className="h-3 w-3" />
                          <span>
                            {copied === offer.couponCode ? "Copied!" : "Copy"}
                          </span>
                        </button>
                      </div>
                    )}
                  <div className="hidden md:flex items-center justify-between text-xs text-gray-500 mb-2">
                    <div className="flex items-center space-x-1">
                      <ClockIcon className="h-3 w-3" />
                      <span>Valid till {offer.validTill}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <UsersIcon className="h-3 w-3" />
                      <span>
                        {typeof offer.usedBy === "number" ? offer.usedBy : 0}{" "}
                        used
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <StarIcon className="h-3 w-3 text-yellow-400" />
                      <span>
                        {typeof offer.rating === "number" ? offer.rating : 0}
                      </span>
                    </div>
                  </div>
                  </div>
                  {/* The mobile button at the very bottom of the card is fixed to use the correct logic now, in case it is ever unhidden. */}
                  <button
                    onClick={() => {
                        // Use the unified click handler for consistency
                        handleDealClick(offer);
                    }}
                    className="w-full bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors duration-200 flex items-center justify-center space-x-2 lg:mt-2 py-2 px-3 text-sm md:py-2 md:px-4 md:text-base rounded-lg hidden"
                  >
                    <span>Get Deal</span>
                    <ExternalLinkIcon className="h-4 w-4" />
                  </button>

                  <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-20 transition-opacity duration-300 bg-gradient-to-br from-primary-200/40 to-primary-400/10 rounded-xl" />
                </div>
              </div>
            ))}
        </div>
      </div >
    </div >
  );
};

export default PopularOffersSection;